import { DepressionContentPage } from "@/components/DepressionContentPage";

const AdultChildWithDepression = () => (
  <DepressionContentPage
    seo={{
      title:
        "My Adult Child Has Depression — What Can I Do? | Neuro Wellness TMS Centers of America",
      description:
        "A guide for parents of adult children struggling with depression. Learn how to start the conversation, explore treatment options, and support without overstepping.",
      canonical: "/adult-child-with-depression",
      keywords:
        "adult child depression, parent helping adult child, treatment-resistant depression young adult, Coral Springs",
    }}
    badge="For Parents"
    heroTitle="My Adult Child Has Depression. What Can I Do?"
    heroSubtitle="Watching your adult child struggle with depression is one of the hardest things a parent can experience. This page is here to help you understand what you can do — and what to avoid — when your child is an adult but still needs support."
    sections={[
      {
        heading: "When Your Child Is an Adult but Still Needs Support",
        variant: "text",
        body: (
          <p>
            The transition from parenting a child to supporting an adult is
            rarely clean. Your child may be 18, 22, or 30 — and you may still
            worry. The difference is that as an adult, they make their own
            medical decisions. Your role shifts from deciding for them to
            standing beside them.
          </p>
        ),
      },
      {
        heading: "What If They Don't Want to Talk About Their Depression?",
        variant: "text",
        body: (
          <p>
            Resistance often comes from exhaustion, fear, or the feeling that
            they've already tried everything. Pushing harder can backfire.
            Instead, keep the door open. Let them know you're there, and that
            there are options they may not have heard of yet. A no today is not
            permanent.
          </p>
        ),
      },
      {
        heading: "What If Medication Isn't Helping?",
        variant: "text",
        body: (
          <p>
            If your adult child has tried one or more antidepressants without
            adequate relief, they may be experiencing treatment-resistant
            depression. This is more common than most people realize, and it
            does not mean they've failed. It means the standard approach hasn't
            reached them — and other mechanisms exist.
          </p>
        ),
      },
      {
        heading: "What If They Don't Take Their Medication Consistently?",
        variant: "callout",
        callout:
          "Do not adjust, manage, or monitor your adult child's medication yourself. Medication decisions belong between the patient and their prescribing clinician. If adherence is a concern, encourage them to discuss it openly with their provider.",
      },
      {
        heading: "When to Explore Other Treatment Options",
        variant: "text",
        body: (
          <p>
            When standard treatments haven't provided enough relief, it may be
            time to learn about interventional approaches. These include TMS,
            accelerated TMS, SAINT®, and SPRAVATO®. Each works differently from
            oral medication. Whether any is appropriate is a clinical
            determination made during evaluation.
          </p>
        ),
      },
      {
        heading: "Can a Parent Call Neuro Wellness Before the Patient Does?",
        variant: "text",
        body: (
          <p>
            Yes. A family member may contact our center to learn general
            information about services and the consultation process. We respect
            patient privacy and consent requirements — the patient must be the
            one to consent to and participate in treatment. But gathering
            information on their behalf is something you can do now.
          </p>
        ),
      },
      {
        heading: "How to Start the Conversation With Your Adult Child",
        variant: "text",
        body: (
          <>
            <p>
              Choose a time that isn't a crisis and isn't an ambush. Share what
              you've learned without framing it as pressure. You might say: "I
              found a center that offers treatments I hadn't heard of before.
              I'm not asking you to commit to anything — I just want you to know
              there are other options."
            </p>
            <p>
              Avoid language that implies they haven't tried hard enough.
              Treatment-resistant depression is a category describing the
              treatments, not a verdict on the person.
            </p>
          </>
        ),
      },
    ]}
    faq={[
      {
        question: "Can I schedule treatment for my adult child?",
        answer:
          "A family member can contact us to learn about services and the consultation process. The patient themselves must consent to and participate in treatment. We encourage involving your adult child in the conversation early.",
      },
      {
        question: "What if my adult child refuses to consider treatment?",
        answer:
          "A no today is not permanent. Keep the door open, share information without pressure, and let them know other options exist. Sometimes the right moment comes later.",
      },
      {
        question: "Will my adult child's information be shared with me?",
        answer:
          "Patient privacy is protected. We can discuss general information about our services with family members, but clinical details require the patient's consent.",
      },
    ]}
    relatedLinks={[
      {
        title: "Helping Someone You Love With Depression",
        href: "/helping-someone-with-depression",
      },
      {
        title: "When Antidepressants Aren't Enough",
        href: "/when-antidepressants-dont-work",
      },
      {
        title: "Depression Treatment Options",
        href: "/depression-treatment-options",
      },
      {
        title: "What Happens During a Consultation",
        href: "/depression-treatment-consultation",
      },
    ]}
    finalCtaHeading="Talk With Our Team About Someone You Love"
    finalCtaText="You can learn about treatment options on behalf of your adult child. Call our team to understand what may be available and what the next step could look like."
  />
);

export default AdultChildWithDepression;
